import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Divider,
    Empty,
    Grid,
    Input,
    List,
    Modal,
    Popconfirm,
    Space,
    Table,
    Tag,
    Typography,
    message,
} from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    PlusOutlined,
    SaveOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
    batchPackagingApi,
    inventoryApi,
    packagingElementsApi,
    productPackagingApi,
} from '../../../api/client';
import { useAuthStore } from '../../../store/auth.store';
import PackagingItemsTable, {
    EditablePackagingItem,
} from './PackagingItemsTable';
import PackagingFiles from './PackagingFiles';
import PackagingComments from './PackagingComments';
import { BatchPackagingDrawer } from './BatchPackagingDrawer';
import type {
    PackagingElement,
    ProductPackagingResponse,
} from '../../bdo/types';
import { MATERIAL_LABELS, formatGrams } from '../../bdo/types';

const { Text } = Typography;
const { useBreakpoint } = Grid;

function toEditable(items: any[]): EditablePackagingItem[] {
    return (items || []).map((item: any, index: number) => ({
        key: item.id || `item-${index}`,
        id: item.id,
        elementId: item.elementId,
        material: item.material,
        massGrams: Number(item.massGrams) || 0,
    }));
}

export default function ProductBdoTab({ productId }: { productId: string }) {
    const queryClient = useQueryClient();
    const screens = useBreakpoint();
    const isMobile = screens.md === false;
    const { hasPermission } = useAuthStore();
    const canEdit = hasPermission('bdo:update') || hasPermission('bdo:manage');

    const [items, setItems] = useState<EditablePackagingItem[]>([]);
    const [includeInBdo, setIncludeInBdo] = useState(true);
    const [note, setNote] = useState('');
    const [elementsModalOpen, setElementsModalOpen] = useState(false);
    const [newElementName, setNewElementName] = useState('');
    const [previewBatchId, setPreviewBatchId] = useState<string | null>(null);
    const [editBatchId, setEditBatchId] = useState<string | null>(null);

    const { data, isLoading } = useQuery<ProductPackagingResponse>({
        queryKey: ['product-packaging', productId],
        queryFn: () => productPackagingApi.get(productId).then((res: any) => res.data),
        enabled: !!productId,
    });

    const { data: elements = [] } = useQuery<PackagingElement[]>({
        queryKey: ['packaging-elements'],
        queryFn: () => packagingElementsApi.list().then((res: any) => res.data),
        enabled: !!productId,
    });

    const { data: batches = [] } = useQuery({
        queryKey: ['product-batches', productId],
        queryFn: () => inventoryApi.getBatches({ productId }).then((res: any) => res.data),
        enabled: !!productId,
    });

    const batchPackagingQueries = useQueries({
        queries: (batches as any[]).map((batch: any) => ({
            queryKey: ['batch-packaging', batch.id],
            queryFn: () => batchPackagingApi.get(batch.id).then((res: any) => res.data),
        })),
    });

    useEffect(() => {
        if (!data) return;
        setItems(toEditable(data.items));
        setIncludeInBdo(data.settings?.includeInBdo !== false);
        setNote(data.settings?.note || '');
    }, [data]);

    const saveMutation = useMutation({
        mutationFn: () => {
            const packed = items
                .filter((item) => item.elementId)
                .map((item, index) => ({
                    elementId: item.elementId,
                    material: item.material,
                    massGrams: Number(item.massGrams) || 0,
                    sortOrder: index,
                }));
            return productPackagingApi.update(productId, {
                includeInBdo,
                note: note || null,
                items: packed,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['product-packaging', productId] });
            queryClient.invalidateQueries({ queryKey: ['bdo-summary'] });
            queryClient.invalidateQueries({ queryKey: ['bdo-sales'] });
            message.success('Kompozycja opakowania zapisana');
        },
        onError: (error: any) =>
            message.error(
                error?.response?.data?.message || 'Nie udało się zapisać kompozycji',
            ),
    });

    const createElementMutation = useMutation({
        mutationFn: (name: string) => packagingElementsApi.create(name),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['packaging-elements'] });
            setNewElementName('');
        },
        onError: () => message.error('Nie udało się dodać elementu'),
    });

    const deleteElementMutation = useMutation({
        mutationFn: (id: string) => packagingElementsApi.delete(id),
        onSuccess: (res: any) => {
            queryClient.invalidateQueries({ queryKey: ['packaging-elements'] });
            if (res.data?.deactivated) {
                message.info('Element jest używany — został ukryty z podpowiedzi.');
            } else {
                message.success('Element usunięty');
            }
        },
        onError: () => message.error('Nie udało się usunąć elementu'),
    });

    const handleCreateElement = async (name: string) => {
        const res = await createElementMutation.mutateAsync(name);
        return res.data as PackagingElement;
    };

    const handleSave = () => {
        if (items.some((item) => !item.elementId)) {
            message.warning('Uzupełnij element opakowania w każdym wierszu.');
            return;
        }
        saveMutation.mutate();
    };

    const previewBatch = (batches as any[]).find((b) => b.id === previewBatchId);
    const previewIndex = (batches as any[]).findIndex((b) => b.id === previewBatchId);
    const previewData =
        previewIndex >= 0 ? (batchPackagingQueries[previewIndex]?.data as any) : null;

    const batchColumns = [
        { title: 'LOT', dataIndex: 'lotNumber', key: 'lotNumber', width: 120 },
        {
            title: 'Ważność',
            dataIndex: 'expiryDate',
            key: 'expiryDate',
            width: 110,
            render: (value: string) => (value ? dayjs(value).format('DD.MM.YYYY') : '—'),
        },
        {
            title: 'Kompozycja',
            dataIndex: 'packagingMode',
            key: 'packagingMode',
            width: 170,
            render: (value: string) => (
                <Tag color={value === 'CUSTOM' ? 'orange' : 'blue'}>
                    {value === 'CUSTOM' ? 'Własna partii' : 'Domyślna produktu'}
                </Tag>
            ),
        },
        {
            title: 'Masa/szt.',
            key: 'mass',
            width: 110,
            align: 'right' as const,
            render: (_: any, record: any) => {
                const index = (batches as any[]).findIndex((b) => b.id === record.id);
                const batchData = index >= 0 ? (batchPackagingQueries[index]?.data as any) : null;
                return batchData
                    ? `${formatGrams(batchData.totals?.totalGrams || 0)} g`
                    : '—';
            },
        },
        {
            title: 'Akcje',
            key: 'actions',
            width: 160,
            render: (_: any, record: any) => (
                <Space size={4}>
                    <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => setPreviewBatchId(record.id)}
                    >
                        Podejrzyj
                    </Button>
                    <Button
                        size="small"
                        type="link"
                        icon={<EditOutlined />}
                        disabled={!canEdit}
                        onClick={() => setEditBatchId(record.id)}
                    >
                        Edytuj
                    </Button>
                </Space>
            ),
        },
    ];

    if (isLoading) {
        return <Text type="secondary">Ładowanie kompozycji BDO...</Text>;
    }

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Card size="small">
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            alignItems: isMobile ? 'stretch' : 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                        }}
                    >
                        <Checkbox
                            checked={includeInBdo}
                            disabled={!canEdit}
                            onChange={(e) => setIncludeInBdo(e.target.checked)}
                        >
                            Licz w BDO
                        </Checkbox>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={saveMutation.isPending}
                            disabled={!canEdit}
                            onClick={handleSave}
                            style={{ width: isMobile ? '100%' : undefined }}
                        >
                            Zapisz kompozycję
                        </Button>
                    </div>

                    {!includeInBdo && (
                        <Alert
                            type="warning"
                            showIcon
                            message="Produkt jest wyłączony z raportu BDO. Jego sprzedaż nie zwiększy masy opakowań."
                        />
                    )}

                    <PackagingItemsTable
                        items={items}
                        elements={elements}
                        onChange={setItems}
                        onCreateElement={handleCreateElement}
                        onManageElements={() => setElementsModalOpen(true)}
                        isMobile={isMobile}
                        disabled={!canEdit}
                    />

                    <div>
                        <Text strong>Notatka</Text>
                        <Input.TextArea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={3}
                            maxLength={2000}
                            disabled={!canEdit}
                            placeholder="Dodatkowe informacje o kompozycji opakowania..."
                            style={{ marginTop: 6 }}
                        />
                    </div>
                </Space>
            </Card>

            <Card size="small">
                <PackagingFiles productId={productId} />
            </Card>

            <Card size="small">
                <PackagingComments productId={productId} isMobile={isMobile} />
            </Card>

            <Card
                size="small"
                title="Partie"
                extra={<Text type="secondary">Kompozycja per partia</Text>}
            >
                <Table
                    dataSource={batches as any[]}
                    columns={batchColumns}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    scroll={{ x: 700 }}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="Brak partii"
                            />
                        ),
                    }}
                />
            </Card>

            {/* Słownik elementów */}
            <Modal
                title="Słownik elementów opakowań"
                open={elementsModalOpen}
                onCancel={() => setElementsModalOpen(false)}
                footer={null}
                width={isMobile ? 'calc(100vw - 16px)' : 520}
                style={isMobile ? { top: 8 } : undefined}
            >
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Space.Compact style={{ width: '100%' }}>
                        <Input
                            placeholder="Nowy element, np. Pipeta"
                            value={newElementName}
                            onChange={(e) => setNewElementName(e.target.value)}
                            onPressEnter={() =>
                                newElementName.trim() &&
                                createElementMutation.mutate(newElementName.trim())
                            }
                        />
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            loading={createElementMutation.isPending}
                            disabled={!newElementName.trim()}
                            onClick={() =>
                                createElementMutation.mutate(newElementName.trim())
                            }
                        >
                            Dodaj
                        </Button>
                    </Space.Compact>

                    <List
                        size="small"
                        dataSource={elements}
                        renderItem={(element: PackagingElement) => (
                            <List.Item
                                actions={[
                                    <Popconfirm
                                        key="delete"
                                        title="Usunąć element?"
                                        description="Jeśli jest używany, zostanie tylko ukryty."
                                        okText="Usuń"
                                        cancelText="Anuluj"
                                        okButtonProps={{ danger: true }}
                                        onConfirm={() =>
                                            deleteElementMutation.mutate(element.id)
                                        }
                                    >
                                        <Button
                                            type="text"
                                            danger
                                            size="small"
                                            icon={<DeleteOutlined />}
                                            disabled={!canEdit}
                                        />
                                    </Popconfirm>,
                                ]}
                            >
                                <Space>
                                    <Text delete={!element.isActive}>{element.name}</Text>
                                    {!element.isActive && <Tag>nieaktywny</Tag>}
                                </Space>
                            </List.Item>
                        )}
                    />
                </Space>
            </Modal>

            {/* Podgląd kompozycji partii */}
            <Modal
                title={`Podgląd opakowania — ${previewBatch?.lotNumber || ''}`}
                open={!!previewBatchId}
                onCancel={() => setPreviewBatchId(null)}
                footer={null}
                width={isMobile ? 'calc(100vw - 16px)' : 640}
                style={isMobile ? { top: 8 } : undefined}
            >
                {previewData ? (
                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                        <Tag color={previewData.mode === 'CUSTOM' ? 'orange' : 'blue'}>
                            {previewData.mode === 'CUSTOM'
                                ? 'Kompozycja własna partii'
                                : 'Kompozycja domyślna produktu'}
                        </Tag>
                        <Table
                            dataSource={previewData.items || []}
                            rowKey={(item: any, index?: number) => item.id || index}
                            size="small"
                            pagination={false}
                            scroll={{ x: 480 }}
                            columns={[
                                {
                                    title: 'Element',
                                    key: 'element',
                                    width: 200,
                                    render: (_: any, item: any) =>
                                        item.element?.name || '—',
                                },
                                {
                                    title: 'Materiał',
                                    dataIndex: 'material',
                                    key: 'material',
                                    width: 160,
                                    render: (value: string) =>
                                        MATERIAL_LABELS[value as keyof typeof MATERIAL_LABELS] ||
                                        value,
                                },
                                {
                                    title: 'Masa [g]',
                                    dataIndex: 'massGrams',
                                    key: 'massGrams',
                                    width: 120,
                                    align: 'right' as const,
                                    render: (value: number) => formatGrams(value),
                                },
                            ]}
                        />
                        <Divider style={{ margin: '8px 0' }} />
                        <Text strong>
                            Razem: {formatGrams(previewData.totals?.totalGrams || 0)} g/szt.
                        </Text>
                    </Space>
                ) : (
                    <Text type="secondary">Ładowanie...</Text>
                )}
            </Modal>

            <BatchPackagingDrawer
                batchId={editBatchId}
                open={!!editBatchId}
                onClose={() => setEditBatchId(null)}
            />
        </Space>
    );
}
